import { StyleSheet, View, ScrollView, Platform, KeyboardAvoidingView } from 'react-native';
import { Text, Button, Surface, useTheme, TextInput } from 'react-native-paper';
import { StatusBar } from 'expo-status-bar';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useOnboarding } from '@/src/context/OnboardingContext';
import { Ionicons } from '@expo/vector-icons';
import { LoadingScreen } from '@/src/components/LoadingScreen';
import { FontAwesome5 } from '@expo/vector-icons';
import { addVehicle } from '@/src/api/apiService';
import { FUEL_TYPE_OPTIONS } from '@/src/lib/constants';
import { SegmentedButtons } from 'react-native-paper';
import FuelTypeSelector from '@/src/components/FuelTypeSelector';

export default function VehicleScreen() {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const { state, setVehicleInfo, fetchVehicleInfo } = useOnboarding();
  
  const [make, setMake] = useState(state.vehicleInfo?.make || '');
  const [model, setModel] = useState(state.vehicleInfo?.model || '');
  const [year, setYear] = useState(state.vehicleInfo?.year || '');
  const [licensePlate, setLicensePlate] = useState(state.vehicleInfo?.licensePlate || '');
  const [fuelType, setFuelType] = useState(state.vehicleInfo?.fuelType || 'gasolina');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExistingVehicle, setHasExistingVehicle] = useState(false);

  const formatRegistrationNumber = (input: string): string => {
    const cleanInput = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const chars = cleanInput.slice(0, 6);
    if (chars.length <= 2) return chars;
    if (chars.length <= 4) return `${chars.slice(0, 2)}-${chars.slice(2)}`;
    return `${chars.slice(0, 2)}-${chars.slice(2, 4)}-${chars.slice(4)}`;
  };

  useEffect(() => {
    fetchVehicleInfo();
  }, []);


  useEffect(() => {
    if (state.vehicleInfo) {
      setMake(state.vehicleInfo.make);
      setModel(state.vehicleInfo.model);
      setYear(state.vehicleInfo.year);
      setLicensePlate(state.vehicleInfo.licensePlate);
      setFuelType(state.vehicleInfo.fuelType);
      setIsFormValid(validateForm());
      
      // Check if user has an existing vehicle
      const hasVehicle = state.vehicleInfo.licensePlate !== '' && 
                        state.vehicleInfo.make !== '';
      setHasExistingVehicle(hasVehicle);
    }
  }, [state.vehicleInfo]);

  const validateForm = () => {
    const licensePlateRegex = /^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/;
    const yearRegex = /^\d{4}$/;
    const currentYear = new Date().getFullYear();
    
    const isLicensePlateValid = licensePlateRegex.test(licensePlate);
    const isYearValid = yearRegex.test(year) && 
                       parseInt(year) >= 1900 && 
                       parseInt(year) <= currentYear;
    const isMakeValid = make.trim() !== '';
    const isModelValid = model.trim() !== '';

    return isLicensePlateValid && isYearValid && isMakeValid && isModelValid;
  };

  const handleLicensePlateChange = (text: string) => {
    const formatted = formatRegistrationNumber(text);
    setLicensePlate(formatted);
  };

  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [licensePlate, year, make, model, fuelType]);

  const handleContinue = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await setVehicleInfo({
        make,
        model,
        year,
        licensePlate,
        fuelType,
      });
      if (result.pendingActivation) {
        router.replace('/dashboard/home');
      } else {
        router.push('/onboarding/permissions');
      }
    } catch (error) {
      console.error('Error adding vehicle:', error);
      alert(t('errorAddingVehicle'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const showForm = !state.vehicleInfo || isEditing;

  return (
    <Surface style={styles.container}>
      <StatusBar style="auto" />
      
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
        <View style={styles.iconContainer}>
          <FontAwesome5 name="car" size={76} color={theme.colors.primary} />
        </View>
        
        <Text style={[styles.title, { color: theme.colors.primary }]}>
          {t('vehicleTitle', 'Informação do Veículo')}
        </Text>
        
        {state.isLoading.vehicle ? (
          <LoadingScreen />
        ) : (
          <>
            {!showForm && state.vehicleInfo ? (
              <View style={styles.infoContainer}>
                <Text style={styles.infoText}>
                  {t('vehicleDataFound', 'Encontramos suas informações de veículo:')}
                </Text>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>{t('make', 'Marca')}:</Text>
                  <Text style={styles.infoValue}>{make}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>{t('model', 'Modelo')}:</Text>
                  <Text style={styles.infoValue}>{model}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>{t('year', 'Ano')}:</Text>
                  <Text style={styles.infoValue}>{year}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>{t('licensePlate', 'Placa')}:</Text>
                  <Text style={styles.infoValue}>{licensePlate}</Text>
                </View>
                {!hasExistingVehicle && (
                  <Button 
                    mode="outlined" 
                    onPress={handleEdit} 
                    style={styles.editButton}
                  >
                    {t('edit', 'Editar')}
                  </Button>
                )}
              </View>
            ) : (
              <View style={styles.formContainer}>
                <Text style={styles.formText}>
                  {t('vehicleFormIntro', 'Por favor, forneça informações sobre seu veículo:')}
                </Text>
                <TextInput
                  label={t('make', 'Marca')}
                  value={make}
                  onChangeText={setMake}
                  style={styles.input}
                  mode="outlined"
                />
                
                <TextInput
                  label={t('model', 'Modelo')}
                  value={model}
                  onChangeText={setModel}
                  style={styles.input}
                  mode="outlined"
                />
                
                <TextInput
                  label={t('year', 'Ano')}
                  value={year}
                  onChangeText={setYear}
                  style={styles.input}
                  mode="outlined"
                  keyboardType="numeric"
                  maxLength={4}
                />
                
                <TextInput
                  label={t('licensePlate', 'Placa')}
                  value={licensePlate}
                  onChangeText={handleLicensePlateChange}
                  style={styles.input}
                  mode="outlined"
                  autoCapitalize="characters"
                  maxLength={8}
                  error={licensePlate.length > 0 && !/^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/.test(licensePlate)}
                />
                {licensePlate.length > 0 && !/^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/.test(licensePlate) && (
                  <Text style={[styles.errorText, { color: theme.colors.error }]}>
                    {t('licensePlateError', 'Formato inválido. Use XX-XX-XX (letras e números)')}
                  </Text>
                )}

                <Text style={{ marginBottom: 5 }}>{t('vehicle.fuelType')}</Text>
                <FuelTypeSelector
                  value={fuelType}
                  onChange={setFuelType}
                  style={{ marginBottom: 20 }}
                />
              </View>
            )}
          </>
        )}
        
        <View style={styles.stepsContainer}>
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.primary }]} />
          <View style={[styles.stepIndicator, { backgroundColor: theme.colors.surfaceVariant }]} />
        </View>
        
        <Button
          mode="contained"
          style={styles.button}
          contentStyle={styles.buttonContent}
          onPress={handleContinue}
          disabled={state.isLoading.vehicle || (showForm && !isFormValid) || isSubmitting}
          loading={isSubmitting}
        >
          {t('continue', 'Continuar')}
        </Button>
      </ScrollView>
      </KeyboardAvoidingView>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
  },
  iconContainer: {
    alignItems: 'center',
    marginVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  formText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  formContainer: {
    marginTop: 16,
    marginBottom: 24,
  },
  input: {
    marginBottom: 16,
  },
  stepsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 24,
  },
  stepIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginHorizontal: 4,
  },
  button: {
    marginTop: 8,
  },
  buttonContent: {
    height: 48,
  },
  loaderContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    textAlign: 'center',
  },
  infoContainer: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
  },
  infoText: {
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  infoLabel: {
    fontWeight: 'bold',
    marginRight: 8,
    flex: 1,
  },
  infoValue: {
    flex: 2,
  },
  editButton: {
    marginTop: 8,
  },
  errorText: {
    fontSize: 12,
    marginTop: -12,
    marginBottom: 16,
    marginLeft: 12,
  },
}); 